import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LettersPanel } from '../components/LettersPanel';
import { BookingsPanel } from '../components/BookingsPanel';
import { AvailabilityPanel } from '../components/AvailabilityPanel';
import { LogOut, Inbox, Mail, Phone, User as UserIcon, Calendar, RefreshCw, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'requests');
  const [letters, setLetters] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [subs, setSubs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c, d, e] = await Promise.all([
        axios.get(`${API}/second-opinion`, { withCredentials: true }),
        axios.get(`${API}/subscribers`, { withCredentials: true }),
        axios.get(`${API}/contact`, { withCredentials: true }),
        axios.get(`${API}/letters`, { withCredentials: true }),
        axios.get(`${API}/intl-bookings`, { withCredentials: true }),
      ]);
      setBookings(e.data);
      setLetters(d.data);
      setRequests(a.data);
      setSubs(b.data);
      setMessages(c.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doLogout = async () => { await logout(); navigate('/login', { replace: true }); };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };

  return (
    <div className="mint-section min-h-[80vh]">
      <div className="container-x py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <span className="grid place-items-center w-12 h-12 rounded-full bg-teal-600 text-white font-semibold">{(user?.name || 'A')[0]}</span>
            )}
            <div>
              <h1 className="font-display text-2xl text-teal-900" style={{ fontWeight: 600 }}>Admin Dashboard</h1>
              <p className="text-slate-500 text-sm">{user?.name} · {user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="btn-outline inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm bg-white"><RefreshCw size={16} /> Refresh</button>
            <button onClick={doLogout} className="btn-coral inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-teal-50 p-6 flex items-center gap-4">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700"><MessageSquare size={22} /></span>
            <div><div data-testid="dashboard-messages-count" className="font-display text-2xl text-teal-900" style={{ fontWeight: 700 }}>{messages.length}</div><div className="text-sm text-slate-500">Contact messages</div></div>
          </div>
          <div className="bg-white rounded-2xl border border-teal-50 p-6 flex items-center gap-4">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700"><Inbox size={22} /></span>
            <div><div className="font-display text-2xl text-teal-900" style={{ fontWeight: 700 }}>{requests.length}</div><div className="text-sm text-slate-500">Second-opinion requests</div></div>
          </div>
          <div className="bg-white rounded-2xl border border-teal-50 p-6 flex items-center gap-4">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700"><Mail size={22} /></span>
            <div><div className="font-display text-2xl text-teal-900" style={{ fontWeight: 700 }}>{subs.length}</div><div className="text-sm text-slate-500">Newsletter subscribers</div></div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => setTab('requests')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'requests' ? 'btn-teal' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`}>Requests</button>
          <button onClick={() => setTab('subs')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'subs' ? 'btn-teal' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`}>Subscribers</button>
          <button data-testid="dashboard-messages-tab" onClick={() => setTab('messages')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'messages' ? 'btn-teal' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`}>Messages</button>
          <button data-testid="dashboard-letters-tab" onClick={() => setTab('letters')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'letters' ? 'btn-teal' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`}>Letters</button>
          <button data-testid="dashboard-bookings-tab" onClick={() => setTab('bookings')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'bookings' ? 'btn-teal' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`}>Online bookings</button>
          <button data-testid="dashboard-availability-tab" onClick={() => setTab('availability')} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'availability' ? 'btn-teal' : 'bg-teal-50 text-teal-700 hover:bg-teal-100'}`}>Availability</button>
        </div>

        {loading ? (
          <p className="text-slate-500 py-10">Loading…</p>
        ) : tab === 'availability' ? (
          <AvailabilityPanel />
        ) : tab === 'bookings' ? (
          <BookingsPanel bookings={bookings} fmt={fmt} reload={load} />
        ) : tab === 'letters' ? (
          <LettersPanel letters={letters} reload={load} />
        ) : tab === 'messages' ? (
          <div className="bg-white rounded-2xl border border-teal-50 overflow-hidden">
            {messages.length === 0 ? (
              <p className="text-slate-500 p-8 text-center">No messages yet.</p>
            ) : messages.map((m) => (
              <div key={m.id} className="p-5 border-b border-teal-50 last:border-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 font-semibold text-teal-900"><UserIcon size={16} className="text-teal-600" /> {m.name}</div>
                  <div className="flex items-center gap-1 text-xs text-slate-400"><Calendar size={13} /> {fmt(m.created_at)}</div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
                  <span className="flex items-center gap-1"><Phone size={13} className="text-teal-600" /> {m.phone}</span>
                  {m.email && <span className="flex items-center gap-1"><Mail size={13} className="text-teal-600" /> {m.email}</span>}
                  {m.location && <span>Location: <strong className="text-slate-700">{m.location}</strong></span>}
                </div>
                <p className="text-sm text-slate-600 mt-2 bg-teal-50/50 rounded-lg p-3">{m.message}</p>
              </div>
            ))}
          </div>
        ) : tab === 'requests' ? (
          <div className="bg-white rounded-2xl border border-teal-50 overflow-hidden">
            {requests.length === 0 ? (
              <p className="text-slate-500 p-8 text-center">No requests yet.</p>
            ) : requests.map((r) => (
              <div key={r.id} className="p-5 border-b border-teal-50 last:border-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 font-semibold text-teal-900"><UserIcon size={16} className="text-teal-600" /> {r.name}</div>
                  <div className="flex items-center gap-1 text-xs text-slate-400"><Calendar size={13} /> {fmt(r.created_at)}</div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-slate-600">
                  <span className="flex items-center gap-1"><Phone size={13} className="text-teal-600" /> {r.phone}</span>
                  {r.email && <span className="flex items-center gap-1"><Mail size={13} className="text-teal-600" /> {r.email}</span>}
                  {r.condition && <span>Condition: <strong className="text-slate-700">{r.condition}</strong></span>}
                </div>
                {r.message && <p className="text-sm text-slate-600 mt-2 bg-teal-50/50 rounded-lg p-3">{r.message}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-teal-50 overflow-hidden">
            {subs.length === 0 ? (
              <p className="text-slate-500 p-8 text-center">No subscribers yet.</p>
            ) : subs.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-4 border-b border-teal-50 last:border-0">
                <span className="flex items-center gap-2 text-slate-700"><Mail size={15} className="text-teal-600" /> {s.email}</span>
                <span className="text-xs text-slate-400">{fmt(s.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
